import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { BrowserRouter, Route, Switch, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { ExternalRedirect } from './components/ExternalRedirect';
import { Footer } from './components/Footer';
import { MobileNav } from './components/MobileNav';
import { Nav } from './components/Nav';
import { useIsMobile } from './hooks/responsive';
import { Page404 } from './pages/Page404';
import { PageAttend } from './pages/PageAttend';
import { PageCovid } from './pages/PageCovid';
import { PageFAQ } from './pages/PageFAQ';
import { PageHome } from './pages/PageHome';
import { INTERNAL_LINKS, REDIRECT_LINKS } from './utils/links';
import { THEME_COLORS } from './utils/theme';

const Container = styled.div`
  position: absolute;
  font-family: 'Work Sans', sans-serif;
  font-weight: 500;
  color: ${THEME_COLORS.text};

  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  min-height: 100vh;
  height: fit-content;
`;

const PageTitle: React.FC<{ title: string }> = props => {
  return (
    <Helmet>
      <title>{props.title}</title>
    </Helmet>
  );
};

const RouteSwitch: React.FC = () => {
  const history = useHistory();

  useEffect(() => {
    return history.listen(() => {
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'auto',
      });
    });
  }, [history]);

  const redirects = Object.values(REDIRECT_LINKS).map(linkConfig => (
    <Route path={linkConfig.link} key={linkConfig.link} exact>
      <ExternalRedirect url={linkConfig.target} />
    </Route>
  ));

  return (
    <Switch>
      <Route path={INTERNAL_LINKS.HOME} exact>
        <PageTitle title="DubHacks '21" />
        <PageHome />
      </Route>
      <Route path={INTERNAL_LINKS.ATTEND} exact>
        <PageTitle title="DubHacks '21: Attend" />
        <PageAttend />
      </Route>
      <Route path={INTERNAL_LINKS.COVID} exact>
        <PageTitle title="DubHacks '21: COVID-19" />
        <PageCovid />
      </Route>
      <Route path={INTERNAL_LINKS.FAQ} exact>
        <PageTitle title="DubHacks '21: FAQ" />
        <PageFAQ />
      </Route>
      {redirects}
      <Route>
        <PageTitle title="DubHacks '21: 404" />
        <Page404 />
      </Route>
    </Switch>
  );
};

export const App: React.FC = () => {
  const { isMobile } = useIsMobile();

  return (
    <BrowserRouter>
      <Container>
        {isMobile ? <MobileNav /> : <Nav />}
        <RouteSwitch />
        <Footer />
      </Container>
    </BrowserRouter>
  );
};
